import {
  BookingStatus,
  CashLocationMutationCategoryType,
  CashLocationMutationType,
  CashLocationType,
  DeviceConnectionTypes,
  DeviceTypes,
  OrderLineTypes,
  OrderTypes,
  PaymentMethods,
  PaymentStatus,
  Permissions,
  PrinterImageMode,
  PrinterLanguage,
  ProductTypes,
  ProductVisibilityTypes,
  Providers,
  RegisterButtonLocation,
  RegisterFunctions,
  ShiftRepeat,
  Status,
  SupportedLanguages,
  TableShapes,
} from "@repo/database";
import { isSameOrBefore } from "@repo/utils";
import { i18n } from "i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import z from "zod";

const baseEmployeeSchema = (i18n?: i18n) =>
  z.object({
    firstName: z.string().min(1, {
      message: i18n?.t("Errors.item_required", {
        item: i18n.t("Employee.first_name"),
      }),
    }),
    lastName: z.string().optional(),
    email: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional()
      .refine(
        (v) => v === undefined || z.string().email().safeParse(v).success,
        {
          message: "Invalid email address",
        },
      ),
    phone: z.string().optional(),
    tag: z.string().optional(),
    employeeGroupId: z.string().uuid({
      message: i18n?.t("Errors.item_required", {
        item: i18n.t("Employee.group"),
      }),
    }),
    language: z.nativeEnum(SupportedLanguages, {
      message: "Invalid language selection",
    }),
    root: z.void(),
  });

export const QRCodeSchema = z.object({
  tag: z.string().min(1),
  target: z.string().min(1),
  userId: z.string().optional(),
  reference: z.string().optional(),
});

export const EmployeeSchema = (i18n?: i18n) =>
  baseEmployeeSchema(i18n).extend({
    pin: z.string().min(2, {
      message: i18n?.t("Errors.item_required", {
        item: i18n.t("Employee.pin"),
      }),
    }),
  });

export const MerchantSchema = (i18n?: i18n) =>
  z.object({
    companyName: z.string(),
    email: z.string(),
    password: z.string().optional(),
    firstName: z.string(),
  });

export const EmployeeSchemaWithId = (i18n?: i18n) =>
  baseEmployeeSchema(i18n).extend({
    id: z.string().uuid(),
    pin: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
  });

export const EmployeeGroupSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    permissions: z.array(z.nativeEnum(Permissions)),
    root: z.void(),
  });

export const EmployeeGroupSchemaWithId = (i18n?: i18n) =>
  EmployeeGroupSchema(i18n).extend({
    id: z.string().uuid(),
  });

export const PaymentMethodSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1, {
      message: i18n?.t("Errors.item_required", {
        item: i18n.t("PaymentMethod.name"),
      }),
    }),
    type: z.nativeEnum(PaymentMethods, {
      message: i18n?.t("Errors.item_required", {
        item: i18n.t("PaymentMethod.type"),
      }),
    }),
    cash_location_id: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    position: z.number(),
    root: z.void(),
  });

export const MollieTerminalTransaction = (i18n?: i18n) =>
  z.object({
    amount: z.number(),
    paymentId: z.string().uuid(),
    terminalId: z.string(),
    reference: z.string(),
  });

export const PayTerminalTransactionSchema = (i18n?: i18n) =>
  z.object({
    ipAddress: z.string(),
    amount: z.number(),
    paymentId: z.string().uuid(),
    terminalId: z.string(),
    serviceId: z.string(),
    reference: z.string().optional(),
  });

export const PaymentMethodSchemaWithId = (i18n?: i18n) =>
  PaymentMethodSchema(i18n).extend({
    id: z.string().uuid(),
  });

export const MiscProductSchema = (i18n?: i18n) => {
  return z.object({
    name: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    price: z.number(),
    courseId: z.string().uuid().optional(),
    categoryId: z.string().uuid(),
    turnoverGroupId: z.string().uuid().optional(),
    vatRate: z
      .string({ message: i18n?.t("Errors.vat_rate_required") })
      .uuid({ message: i18n?.t("Errors.print_route_required") }),
    printRouteId: z
      .string({ message: i18n?.t("Errors.print_route_required") })
      .uuid({ message: i18n?.t("Errors.print_route_required") }),
  });
};

const baseTableCombinationSchema = z.object({
  groupSize: z.number(),
  priority: z.number().optional(),
  tables: z.array(z.string().uuid()),
  seats: z.number(),
});

export const TableCombinationSchema = (i18n?: i18n) =>
  baseTableCombinationSchema.superRefine((data, ctx) => {
    if (data.groupSize > data.seats) {
      ctx.addIssue({
        path: ["groupSize"],
        code: "custom",
        message: "Group size exceeds the number of seats.",
      });
    }
  });

export const TableCombinationSchemaWithId = (i18n?: i18n) =>
  baseTableCombinationSchema.extend({
    id: z.string().uuid(),
  });

const baseTableSchema = z.object({
  reference: z.string().min(1),
  position: z.number(),
  minSeats: z.number(),
  maxSeats: z.number(),
  bookable: z.boolean(),
  locationId: z.string().uuid(),
  status: z.nativeEnum(Status),
  shape: z.nativeEnum(TableShapes),
  priceId: z
    .string()
    .transform((v) => (v === "" ? undefined : v))
    .optional(),
});

export const TableSchema = baseTableSchema.superRefine((data, ctx) => {
  if (data.minSeats > data.maxSeats) {
    ctx.addIssue({
      path: ["minSeats"],
      code: "custom",
      message: "Min seats must be smaller than max seats",
    });
    ctx.addIssue({
      path: ["maxSeats"],
      code: "custom",
      message: "Max seats must be higher than min seats",
    });
  }
});

export const TableSchemaWithId = baseTableSchema
  .extend({
    id: z.string().uuid(),
  })
  .superRefine((data, ctx) => {
    if (data.minSeats > data.maxSeats) {
      ctx.addIssue({
        path: ["minSeats"],
        code: "custom",
        message: "Min seats must be smaller than max seats",
      });
      ctx.addIssue({
        path: ["maxSeats"],
        code: "custom",
        message: "Max seats must be higher than min seats",
      });
    }
  });

export const FloorPlanLayoutUpdateSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      reference: z.string(),
      locationId: z.string().uuid(),
      position: z.number(),
      rotation: z.number(),
      width: z.number().optional(),
      height: z.number().optional(),
      posX: z.number(),
      posY: z.number(),
    }),
  ),
});

export const ProductModifierSchema = z.object({
  name: z.string().min(1),
  modifierGroupIds: z.array(z.string().uuid()),
  vatRateId: z.string().uuid(),
  turnoverGroupId: z.string().uuid().optional(),
  position: z.number(),
  prices: z.array(
    z.object({
      priceId: z.string().uuid(),
      value: z.number(),
    }),
  ),
});

export const CashRegisterConfigurationSchema = (i18n?: i18n) =>
  z.object({
    tableInfoPopup: z.boolean(),
    defaultPriceId: z.string(),
    orderAttentionCheck: z.number(),
    orderAttentionLate: z.number(),
    nextRoundPrintRouteId: z.string().uuid().optional(),
    autoFinalize: z.boolean(),
  });

export const EmailSettingsSchema = (i18n?: i18n) =>
  z.object({
    smtpHost: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    smtpPort: z.number().optional(),
    smtpUsername: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    smtpPassword: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    smtpFrom: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
  });

const baseProductSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1),
    color: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    status: z.nativeEnum(Status),
    visibility: z.array(z.nativeEnum(ProductVisibilityTypes)),
    preparationName: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    vatRateId: z.string().uuid(),
    notifyPrintRouteId: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    printRouteId: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    courseId: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    categoryId: z.string().uuid(),
    position: z.number(),
    modifierGroupIds: z.array(z.string().uuid()),
    prices: z.array(
      z.object({
        priceId: z.string().uuid(),
        value: z.number(),
      }),
    ),
    description: z
      .string()
      .optional()
      .transform((v) => (v === "" ? undefined : v)),
    turnoverGroupId: z.string().optional(),
  });

export const ProductSchema = (i18n?: i18n) => baseProductSchema(i18n);

const baseServiceSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    locales: z.array(
      z.object({
        language: z.nativeEnum(SupportedLanguages),
        name: z.string(),
        description: z.string(),
      }),
    ),
    status: z.nativeEnum(Status),
  });

export const ServiceSchema = (i18n?: i18n) => baseServiceSchema(i18n);

export const UpdateServiceSchema = (i18n?: i18n) =>
  baseServiceSchema(i18n).partial().extend({
    id: z.string().uuid(),
  });

export const UpdateProductSchema = (i18n?: i18n) =>
  baseProductSchema(i18n).partial().extend({
    id: z.string().uuid(),
    updatedBy: z.string().optional(),
    categoryId: z.string().uuid(),
  });

export const DeviceSchema = z.object({
  name: z.string(),
  type: z.nativeEnum(DeviceTypes),
  deviceId: z.string(),
  pushToken: z.string().optional(),
});

export const TicketSchema = z.object({
  init: z.object({
    connectionType: z.nativeEnum(DeviceConnectionTypes),
    ipAddress: z.string().optional().nullable(),
    systemName: z.string().optional().nullable(),
    port: z.number().optional().nullable(),
  }),
  data: z.any(),
});

export const CashLocationSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1),
    type: z.nativeEnum(CashLocationType),
    root: z.void(),
  });

export const CashLocationSchemaWithId = (i18n?: i18n) =>
  CashLocationSchema(i18n).extend({
    id: z.string().uuid(),
  });

// export const PaymentSchema = z.object({
//   terminalId: z.string(),
//   amount: z.number(),
//   currency: z.string(),
//   paymentMethodId: z.string().uuid(),
//   orderId: z.string().uuid(),
// });

export const MollieSchema = z.object({
  profileId: z.string(),
});

export const ProductCategorySchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    color: z
      .string()

      .optional()
      .refine(
        (val) => val === undefined || val === "" || /^#[0-9a-f]{6}$/i.test(val),
        {
          message: i18n?.t("Errors.no_valid_color"),
        },
      ),
    position: z.number(),
    root: z.void(),
    modifierGroupIds: z.array(z.string().uuid()),
    turnoverGroupId: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    courseId: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    vatRateId: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    printRouteId: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    notifyPrintRouteId: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
  });

export const ProductCategorySchemaWithId = ProductCategorySchema().extend({
  id: z.string(),
});

export const CourseSchema = z.object({
  name: z.string().min(1),
  color: z.string().min(1),
  position: z.number(),
  root: z.void(),
});

export const VatRateSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    value: z.number(),
  });

export const LabelSchema = (i18n?: i18n) =>
  z.object({
    text: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    color: z.string(),
    position: z.number(),
    root: z.void(),
  });

export const Timeslot = z.object({
  id: z.string().uuid().optional(),
  originalId: z.string().uuid().optional(),
  temporary: z.boolean(),
  start: z.date(),
  open: z.boolean(),
  capacity: z.number(),
  spots: z.number(),
  duration: z.number(),
  maxGroupSize: z.number().optional(),
});

export const DaySchema = (i18n?: i18n) =>
  z.object({
    closed: z.boolean().optional(),
    startDate: z.date(),
    endDate: z.date(),
    timeslots: z.array(Timeslot),
    message: z
      .string()
      .optional()
      .transform((v) => (v === "" ? undefined : v)),
  });

export const CompanySchema = (i18n?: i18n) =>
  z.object({
    companyName: z.string().optional(),
    zipCode: z.string(),
    cocNumber: z.string().optional(),
    city: z.string(),
    streetName: z.string(),
    streetNumber: z.string(),
    country: z.string(),
    phoneNumber: z.string(),
    email: z.string(),
    website: z.string().optional(),
  });

export const SectionSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    position: z.number().min(1),
    root: z.void(),
    width: z.number(),
    height: z.number(),
  });

export const SectionSchemaWithId = SectionSchema().extend({
  id: z.string().uuid(),
});

export const ButtonSchema = z.object({
  position: z.number(),
  location: z.nativeEnum(RegisterButtonLocation),
  mobile: z.boolean(),
  function: z.nativeEnum(RegisterFunctions),
  color: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  label: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
});

export const ButtonSchemaWithId = ButtonSchema.extend({
  id: z.string().uuid(),
});

export const OrderSettingsSchema = z.object({
  name: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  partySize: z.number().optional(),
  labelIds: z.array(z.string()),
  bookingId: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
});

const orderLines = z.array(
  z.object({
    name: z.string(),
    value: z.number(),
    reference: z.string(),
    quantity: z.number(),
    createdAt: z.date(),
    vatRate: z.number(),
    vatRateName: z.string().optional(),
    type: z.nativeEnum(OrderLineTypes),
    parentReference: z.string().optional(),
    productId: z.string().uuid().optional(),
    categoryId: z.string().uuid().optional(),
    categoryName: z.string().optional(),
    courseName: z.string().optional(),
    employeeId: z.string().uuid().optional(),
    printRouteId: z.string().uuid().optional(),
    notifyPrintRouteId: z.string().uuid().optional(),
    preparationName: z.string().optional(),
    courseId: z.string().uuid().optional(),
    turnoverGroupName: z.string().optional(),
    turnoverGroupLedgerNumber: z.number().optional(),
    orderId: z.string().uuid().optional(),
  }),
);

export const PaymentSchema = z.object({
  id: z.string().uuid(),
  amount: z.number(),
  paymentMethodId: z.string().min(1),
  paymentMethodName: z.string().optional(),
  employeeId: z.string().uuid().optional(),
  paymentStatus: z.nativeEnum(PaymentStatus).optional(),
  createdAt: z.date(),
  tip: z.number(),
  change: z.number(),
});

export const OrderSchema = z.object({
  id: z.string().uuid(),
  tableId: z.string().uuid().optional().nullable(),
  updatedAt: z.date(),
  employeeId: z.string().uuid().optional(),
  orderLines,
  partySize: z.number().optional(),
  labelIds: z.array(z.string()),
  payments: z.array(PaymentSchema),
  printTicket: z.boolean().optional(),
  name: z
    .string()
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
});

export const CheckoutSchema = z.object({
  order: OrderSchema,
  payment: PaymentSchema,
});

export const TransferItemsSchema = z.object({
  items: orderLines,
  tableId: z.string().uuid(),
  orderId: z.string().uuid(),
  combine: z.boolean(),
});

export const CashLocationMutationCategorySchema = z.object({
  name: z.string().min(1),
  type: z.nativeEnum(CashLocationMutationCategoryType),
});

export const MutationSchema = (i18n?: i18n) =>
  z.object({
    value: z.number(),
    type: z.nativeEnum(CashLocationMutationType),
    vatRate: z.number().optional(),
    cashLocationId: z.string().uuid(),
    employeeId: z.string().optional(),
    category: z.string().optional(),
    description: z
      .string()
      .optional()
      .transform((v) => (v === "" ? undefined : v)),
    root: z.void(),
  });
export const WidgetBookingSchema = (i18n?: i18n) =>
  z.object({
    groupSize: z.number(),
    serviceId: z.string().optional(),
    timeslot: z.coerce.date(),
    comment: z.string().optional(),
    customer: z.object({
      firstName: z.string().min(1, i18n?.t("Validation.first_name_min_length")),
      lastName: z.string().min(1, i18n?.t("Validation.last_name_min_length")),
      phone: z
        .string()
        .min(1, i18n?.t("Validation.phone_min_length"))
        .superRefine((phone, ctx) => {
          if (!phone) return phone;
          if (!isValidPhoneNumber(phone)) {
            ctx.addIssue({
              code: "custom",
              message: i18n?.t("Validation.phone_invalid"),
            });
          }
        }),
      email: z
        .string()
        .min(1, i18n?.t("Validation.email_min_length"))
        .email(i18n?.t("Validation.invalid_email")),
      newsletter: z.boolean(),
      language: z.custom<SupportedLanguages>(),
    }),
  });

const baseOrderShiftSchema = (i18n?: i18n) =>
  z.object({
    startTime: z.date(),
    endTime: z.date(),
    orderType: z.nativeEnum(OrderTypes),
    startDate: z.date(),
    endDate: z.date().optional(),
    timeslotInterval: z.number(),
    repeat: z.nativeEnum(ShiftRepeat),
    repeatDays: z.array(z.number()),
    timeslots: z.array(
      z.object({
        start: z.date(),
        spots: z.number(),
        closed: z.boolean(),
      }),
    ),
  });

export const OrderShiftSchema = (i18n?: i18n) =>
  baseOrderShiftSchema(i18n).superRefine((data, ctx) => {
    if (data.endDate) {
      if (data.endDate.getTime() < data.startDate.getTime()) {
        ctx.addIssue({
          path: ["endDate"],
          code: "custom",
          message: "End date must be after start date",
        });
      }
    }
  });

export const OrderShiftSchemaWithId = (i18n?: i18n) =>
  baseOrderShiftSchema(i18n)
    .extend({ id: z.string().uuid() })
    .superRefine((data, ctx) => {
      if (data.endDate) {
        if (data.endDate.getTime() < data.startDate.getTime()) {
          ctx.addIssue({
            path: ["endDate"],
            code: "custom",
            message: "End date must be after start date",
          });
        }
      }
    });

const baseShiftSchema = (i18n?: i18n) =>
  z.object({
    serviceId: z.string().uuid(),
    startTime: z.date(),
    endTime: z.date(),
    startDate: z.date(),
    endDate: z.date().optional(),
    minPartySize: z.number(),
    maxPartySize: z.number(),
    defaultDuration: z.number(),
    reservationGap: z.number(),
    timeslotInterval: z.number(),
    repeat: z.nativeEnum(ShiftRepeat),
    capacity: z.number(),
    defaultMaxGuests: z.number().optional(),
    maxReservations: z.number().optional(),
    sectionIds: z.array(z.string()),
    repeatDays: z.array(z.number()),
    timeslots: z.array(
      z.object({
        start: z.date(),
        duration: z.number(),
        spots: z.number(),
        closed: z.boolean(),
      }),
    ),
  });

export const ShiftSchema = (i18n?: i18n) =>
  baseShiftSchema(i18n).superRefine((data, ctx) => {
    if (data.endDate) {
      if (data.endDate.getTime() < data.startDate.getTime()) {
        ctx.addIssue({
          path: ["endDate"],
          code: "custom",
          message: "End date must be after start date",
        });
      }
    }
    if (data.minPartySize > data.maxPartySize) {
      ctx.addIssue({
        path: ["minPartySize"],
        code: "custom",
        message: "Min party size must be smaller than max party size",
      });
      ctx.addIssue({
        path: ["maxPartySize"],
        code: "custom",
        message: "Max party size must be higher than min party size",
      });
    }
  });

export const ShiftSchemaWithId = (i18n?: i18n) =>
  baseShiftSchema(i18n)
    .extend({ id: z.string().uuid() })
    .superRefine((data, ctx) => {
      if (data.endDate) {
        if (data.endDate.getTime() < data.startDate.getTime()) {
          ctx.addIssue({
            path: ["endDate"],
            code: "custom",
            message: "End date must be after start date",
          });
        }
      }
      if (data.minPartySize > data.maxPartySize) {
        ctx.addIssue({
          path: ["minPartySize"],
          code: "custom",
          message: "Min party size must be smaller than max party size",
        });
        ctx.addIssue({
          path: ["maxPartySize"],
          code: "custom",
          message: "Max party size must be higher than min party size",
        });
      }
    });

export const SplitOrderSchema = (i18n?: i18n) =>
  z.object({
    orderId: z.string().uuid(),
    tableId: z.string().uuid().optional(),
    orderLines: z.array(z.string()),
    employeeId: z.string().uuid().optional(),
  });
export const MoveBookingSchema = z.object({
  id: z.string().uuid(),
  tableIds: z.array(z.string().uuid()),
});

export const UpdateBookingStatusSchema = z.object({
  id: z.string().uuid(),
  bookingStatus: z.nativeEnum(BookingStatus),
  root: z.void(),
});

const baseBookingSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  startTime: z.date(),
  endTime: z.date(),
  bookingStatus: z.nativeEnum(BookingStatus),
  status: z.nativeEnum(Status),
  groupSize: z.number(),
  tableIds: z.array(z.string()),
  phone: z.string().optional(),
  email: z.string().optional(),
  comment: z.string().optional(),
  memo: z.string().optional(),
  labelIds: z.array(z.string()),
  starred: z.boolean().optional(),
  activeShiftWarning: z.void(),
  overlapWarning: z.void(),
  endTimeBeforeStartError: z.void(),
});

export const BookingSchema = baseBookingSchema
  .extend({
    createdBy: z.string().optional(),
    provider: z.nativeEnum(Providers),
    createdAt: z.date(),
  })
  .superRefine((data, ctx) => {
    if (isSameOrBefore(data.endTime, data.startTime)) {
      ctx.addIssue({
        path: ["endTimeBeforeStartError"],
        code: "custom",
        message: "End time must be after start time",
      });
    }
  });

export const UpdateBookingSchema = baseBookingSchema
  .partial()
  .extend({
    id: z.string().uuid(),
    updatedBy: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.startTime &&
      data.endTime &&
      isSameOrBefore(data.endTime, data.startTime)
    ) {
      ctx.addIssue({
        path: ["endTimeBeforeStartError"],
        code: "custom",
        message: "End time must be after start time",
      });
    }
  });

export const PriceSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    root: z.void(),
  });

export const PriceSchemaWithId = (i18n?: i18n) =>
  PriceSchema(i18n).extend({
    id: z.string().uuid(),
  });

export const PrintRouteSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    printerIds: z
      .array(z.string().uuid())
      .min(1, { message: i18n?.t("Errors.printer_required") }),
    root: z.void(),
  });

export const PrintRouteSchemaWithId = (i18n?: i18n) =>
  PrintRouteSchema(i18n).extend({
    id: z.string().uuid(),
  });

const validatePrinter =
  (i18n?: i18n) =>
  (
    { connectionType, ipAddress, port, systemName }: any,
    ctx: z.RefinementCtx,
  ) => {
    if (connectionType === DeviceConnectionTypes.network && !ipAddress) {
      ctx.addIssue({
        path: ["ipAddress"],
        code: "custom",
        message: i18n?.t("Errors.printer_ip_address_required"),
      });
    }
    if (connectionType === DeviceConnectionTypes.network && !port) {
      ctx.addIssue({
        path: ["port"],
        code: "custom",
        message: i18n?.t("Errors.printer_port_required"),
      });
    }
    if (connectionType === DeviceConnectionTypes.usb && !systemName) {
      ctx.addIssue({
        path: ["systemName"],
        code: "custom",
        message: i18n?.t("Errors.printer_system_name_required"),
      });
    }
  };

const basePrinterSchema = (i18n?: i18n) =>
  z.object({
    name: z.string().min(1, { message: i18n?.t("Errors.name_required") }),
    connectionType: z.nativeEnum(DeviceConnectionTypes),
    width: z.number().min(1),
    language: z.nativeEnum(PrinterLanguage),
    imageMode: z.nativeEnum(PrinterImageMode),
    systemName: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    copies: z.number().min(1),
    ipAddress: z
      .string()
      .transform((v) => (v === "" ? undefined : v))
      .optional(),
    hasCashDrawer: z.boolean(),
    port: z.number().optional(),
    root: z.void(),
  });

export const PrinterSchema = (i18n?: i18n) =>
  basePrinterSchema(i18n).superRefine(validatePrinter(i18n));

export const PrinterSchemaWithId = basePrinterSchema()
  .extend({
    id: z.string().uuid(),
  })
  .superRefine(validatePrinter());

export const ProductModifierGroupSchema = z.object({
  name: z.string().min(1),
});
